// /stacks/traffic/dev/frontend/src/lib/urlState.js

// All times we place on the URL or send to the API are strings in
// 'YYYY-MM-DD HH:MM:SS' (LOCAL time).

const pad2 = (n) => String(n).padStart(2, "0");

function parseLocalYMDHMS(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const Y = Number(m[1]), M = Number(m[2]), D = Number(m[3]);
  const h = Number(m[4]), mi = Number(m[5]), se = Number(m[6] ?? "0");
  return new Date(Y, M - 1, D, h, mi, se, 0);
}

export function fmtAPIDate(v) {
  const d = v instanceof Date
    ? v
    : (typeof v === "string" ? (parseLocalYMDHMS(v) || new Date(v)) : new Date());
  if (!d || isNaN(d)) return "";
  const yyyy = d.getFullYear();
  const MM = pad2(d.getMonth() + 1);
  const DD = pad2(d.getDate());
  const HH = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  const ss = pad2(d.getSeconds());
  return `${yyyy}-${MM}-${DD} ${HH}:${mm}:${ss}`;
}

// For <input type="datetime-local"> — returns 'YYYY-MM-DDTHH:MM' local.
export function fmtDateTimeLocal(v) {
  const d = v instanceof Date
    ? v
    : (typeof v === "string" ? (parseLocalYMDHMS(v) || new Date(v)) : new Date());
  if (!d || isNaN(d)) return "";
  const yyyy = d.getFullYear();
  const MM = pad2(d.getMonth() + 1);
  const DD = pad2(d.getDate());
  const HH = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  return `${yyyy}-${MM}-${DD}T${HH}:${mm}`;
}

function startOfDay(d)  { const x = new Date(d); x.setHours(0,0,0,0);  return x; }
function endOfDay(d)    { const x = new Date(d); x.setHours(24,0,0,0); return x; }
function startOfWeek(d) { const x = startOfDay(d); const diff = x.getDay(); x.setDate(x.getDate() - diff); return x; }
function endOfWeek(d)   { const x = startOfWeek(d); x.setDate(x.getDate() + 7); return x; }
function startOfMonth(d){ return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0); }
function endOfMonth(d)  { return new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0); }
function startOfYear(d) { return new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0); }
function endOfYear(d)   { return new Date(d.getFullYear() + 1, 0, 1, 0, 0, 0, 0); }

export function rangeForPeriod(period, now = new Date()) {
  switch ((period || "").toLowerCase()) {
    case "today":      return { start: fmtAPIDate(startOfDay(now)),  end: fmtAPIDate(endOfDay(now)) };
    case "this_week":  return { start: fmtAPIDate(startOfWeek(now)), end: fmtAPIDate(endOfWeek(now)) };
    case "last_week": {
      const e = startOfWeek(now); const s = new Date(e); s.setDate(s.getDate() - 7);
      return { start: fmtAPIDate(s), end: fmtAPIDate(e) };
    }
    case "this_month": return { start: fmtAPIDate(startOfMonth(now)), end: fmtAPIDate(endOfMonth(now)) };
    case "last_month": {
      const s = startOfMonth(now); const ss = new Date(s); ss.setMonth(ss.getMonth() - 1);
      return { start: fmtAPIDate(ss), end: fmtAPIDate(s) };
    }
    case "this_year":  return { start: fmtAPIDate(startOfYear(now)), end: fmtAPIDate(endOfYear(now)) };
    case "last_year":  { const s = startOfYear(now); const ss = new Date(s); ss.setFullYear(ss.getFullYear() - 1); return { start: fmtAPIDate(ss), end: fmtAPIDate(s) }; }
    case "any":
    case "anytime":
    case "all_time": {
      const s = new Date(2000, 0, 1); const e = new Date(2100, 0, 1);
      return { start: fmtAPIDate(s), end: fmtAPIDate(e) };
    }
    case "custom":
    default:
      return null;
  }
}

export function readFromURL(search) {
  const p = new URLSearchParams(search || "");
  const period = p.get("period") || "";
  const start  = p.get("start")  || "";
  const end    = p.get("end")    || "";
  const cams   = (p.get("cameras") || p.get("camera") || "")
                 .split(",").filter(Boolean);
  const plate  = p.get("plate") || "";
  const exact  = (p.get("exact") || "").toLowerCase() === "true";
  const buckets = (p.get("buckets") || "").split(",").filter(Boolean);
  const is_night_param = p.get("is_night");
  const is_night = (is_night_param === null) ? null : (is_night_param === "1" || is_night_param === "true");
  const limit  = Number(p.get("limit") || 100);
  const offset = Number(p.get("offset") || 0);
  const order  = (p.get("order") || "desc").toLowerCase() === "asc" ? "asc" : "desc";
  return { period, start, end, cameras: cams, plate, exact, buckets, is_night, limit, offset, order };
}

export function writeToURL(state, replace = true) {
  const p = new URLSearchParams();
  const set = (k, v) => { if (v !== undefined && v !== null && String(v).length) p.set(k, String(v)); };

  if (state.start && state.end) set("period", "custom");
  set("start", state.start);
  set("end",   state.end);
  if (state.cameras?.length) set("cameras", state.cameras.join(","));

  // Only one of buckets / is_night
  if (state.is_night === true || state.is_night === false) {
    set("is_night", state.is_night ? "1" : "0");
  } else if (state.buckets?.length) {
    set("buckets", state.buckets.join(","));
  }

  if (state.plate) set("plate", state.plate);
  if (state.exact) set("exact", "true");

  set("limit",  state.limit ?? 100);
  set("offset", state.offset ?? 0);
  set("order",  state.order ?? "desc");

  const url = `${location.pathname}?${p.toString()}`;
  if (replace) history.replaceState(null, "", url);
  else history.pushState(null, "", url);
}
